import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  MdClose,
  MdSave,
  MdSettings,
  MdDashboard,
  MdBusiness,
  MdLocationOn,
  MdGroup,
  MdVerifiedUser,
  MdPerson,
  MdPhoneAndroid,
  MdPayment,
  MdReceipt,
  MdSwapHoriz,
  MdHistory,
  MdRefresh
} from 'react-icons/md';
import { API_BASE_URL, createApiConfig } from '../config/api';

const UserSidebarPermissionsModal = ({ isOpen, onClose, user, showPopup }) => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasUserPermissions, setHasUserPermissions] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  // Icon mapping for menu items
  const iconMap = {
    dashboard: MdDashboard,
    users: MdPerson,
    manage_mmdas: MdBusiness,
    zone_management: MdLocationOn,
    my_zones: MdLocationOn,
    groups: MdGroup,
    revenue_officers: MdVerifiedUser,
    traders: MdBusiness,
    devices: MdPhoneAndroid,
    payment_methods: MdPayment,
    receipts: MdReceipt,
    transactions: MdSwapHoriz,
    audit_logs: MdHistory,
    settings: MdSettings
  };

  useEffect(() => {
    if (isOpen && user) {
      fetchPermissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  const fetchPermissions = async () => {
    setLoading(true);
    setError('');
    try {
      // First check if user has specific permissions
      const userPermissionsResponse = await axios.get(`${API_BASE_URL}/sidebar-permissions/user/${user.user_id}`, createApiConfig());

      if (userPermissionsResponse.data.length > 0) {
        // User has specific permissions
        setPermissions(userPermissionsResponse.data);
        setHasUserPermissions(true);
      } else {
        // User doesn't have specific permissions, get role-based permissions
        // Handle case mismatch: users have "mmda" but permissions are stored as "MMDA"
        let roleName = user.role_name;
        if (roleName === 'mmda') {
          roleName = 'MMDA';
        }

        const rolePermissionsResponse = await axios.get(`${API_BASE_URL}/sidebar-permissions/role/${roleName}`, createApiConfig());
        setPermissions(rolePermissionsResponse.data);
        setHasUserPermissions(false);
      }
    } catch (err) {
      setError('Failed to fetch permissions');
      console.error('Error fetching permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const initializeUserPermissions = async () => {
    setLoading(true);
    setError('');
    try {
      // Create user-specific permissions based on current selections in the modal
      const menuItems = permissions.map(p => ({
        menu_item_key: p.menu_item_key,
        menu_item_name: p.menu_item_name,
        menu_item_endpoint: p.menu_item_endpoint,
        parent_menu_key: p.parent_menu_key,
        is_visible: p.is_visible, // Use current selection from modal
        display_order: p.display_order
      }));

      // Handle case mismatch for role name
      let roleName = user.role_name;
      if (roleName === 'mmda') {
        roleName = 'MMDA';
      }

      await axios.post(`${API_BASE_URL}/sidebar-permissions/user/${user.user_id}/initialize`, {
        roleName: roleName,
        menuItems
      }, createApiConfig());

      // Update state to reflect that user now has custom permissions
      setHasUserPermissions(true);

      showPopup('User-specific permissions created successfully!', 'success');
      onClose();
    } catch (err) {
      setError('Failed to initialize user permissions');
      showPopup('Failed to initialize user permissions', 'error');
      console.error('Error initializing user permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (permissionId) => {
    setPermissions(prev => {
      const newPermissions = prev.map(permission =>
        permission.permission_id === permissionId
          ? { ...permission, is_visible: !permission.is_visible }
          : permission
      );

      const target = newPermissions.find(p => p.permission_id === permissionId);

      if (target.is_visible) {
        // If child, auto-check parent
        if (target.parent_menu_key) {
          const parentIndex = newPermissions.findIndex(p => p.menu_item_key === target.parent_menu_key);
          if (parentIndex !== -1) {
            newPermissions[parentIndex] = { ...newPermissions[parentIndex], is_visible: true };
          }
        } else {
          // If parent, auto-check all children
          newPermissions.forEach(p => {
            if (p.parent_menu_key === target.menu_item_key) {
              p.is_visible = true;
            }
          });
        }
      }

      return newPermissions;
    });
  };

  const savePermissions = async () => {
    setSaving(true);
    setError('');
    try {
      if (hasUserPermissions) {
        // Get only the permissions that are currently selected/checked (is_visible: true)
        const selectedPermissions = permissions
          .filter(p => p.is_visible === true)
          .map(p => ({
            menu_item_key: p.menu_item_key
          }));

        // Use the new endpoint that sets selected to visible and others to hidden
        await axios.put(`${API_BASE_URL}/sidebar-permissions/user/${user.user_id}/selected`, {
          selectedPermissions: selectedPermissions
        }, createApiConfig());

        const totalPermissions = permissions.length;
        const hiddenCount = totalPermissions - selectedPermissions.length;
        showPopup(`Updated permissions: ${selectedPermissions.length} visible, ${hiddenCount} hidden`, 'success');
      } else {
        // First initialize user permissions with current selections
        await initializeUserPermissions();
        return; // initializeUserPermissions will handle the success message and close modal
      }

      onClose();
    } catch (err) {
      setError('Failed to save permissions');
      showPopup('Failed to save permissions', 'error');
      console.error('Error saving permissions:', err);
    } finally {
      setSaving(false);
    }
  };

  const selectAllPermissions = () => {
    setPermissions(prev =>
      prev.map(permission => ({ ...permission, is_visible: true }))
    );
  };

  const deselectAllPermissions = () => {
    setPermissions(prev =>
      prev.map(permission => ({ ...permission, is_visible: false }))
    );
  };

  const groupPermissionsByParent = () => {
    // Filter permissions based on search
    const filteredPermissions = permissions.filter(permission =>
      permission.menu_item_name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      (permission.menu_item_endpoint && permission.menu_item_endpoint.toLowerCase().includes(searchFilter.toLowerCase()))
    );

    const grouped = {};
    filteredPermissions.forEach(permission => {
      const parent = permission.parent_menu_key || 'root';
      if (!grouped[parent]) {
        grouped[parent] = [];
      }
      grouped[parent].push(permission);
    });

    // Sort by display order
    Object.keys(grouped).forEach(key => {
      grouped[key].sort((a, b) => a.display_order - b.display_order);
    });

    return grouped;
  };

  const renderPermissionItem = (permission, isChild = false) => {
    const IconComponent = iconMap[permission.menu_item_key] || MdSettings;

    return (
      <div
        key={permission.permission_id}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: isChild ? '8px 16px 8px 40px' : '12px 16px',
          backgroundColor: isChild ? '#f8f9fa' : '#fff',
          borderLeft: isChild ? '3px solid #e9ecef' : 'none',
          borderBottom: '1px solid #e9ecef',
          cursor: 'pointer',
          transition: 'background-color 0.2s ease'
        }}
        onClick={() => togglePermission(permission.permission_id)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = isChild ? '#e9ecef' : '#f8f9fa';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = isChild ? '#f8f9fa' : '#fff';
        }}
      >
        {/* Checkbox */}
        <input
          type="checkbox"
          checked={permission.is_visible}
          onChange={() => togglePermission(permission.permission_id)}
          style={{
            marginRight: 12,
            width: 16,
            height: 16,
            cursor: 'pointer',
            accentColor: '#1976d2'
          }}
          onClick={(e) => e.stopPropagation()}
        />

        {/* Menu Icon */}
        <IconComponent
          size={18}
          style={{
            color: permission.is_visible ? '#1976d2' : '#9e9e9e',
            marginRight: 12,
            minWidth: 18
          }}
        />

        {/* Menu Item Details */}
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: isChild ? 'normal' : '500',
            fontSize: isChild ? '14px' : '15px',
            color: permission.is_visible ? '#333' : '#9e9e9e'
          }}>
            {permission.menu_item_name}
          </div>
          {permission.menu_item_endpoint && (
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '2px'
            }}>
              {permission.menu_item_endpoint}
            </div>
          )}
        </div>

        {/* Status Badge */}
        <div style={{
          padding: '2px 8px',
          borderRadius: 12,
          fontSize: '11px',
          fontWeight: '500',
          backgroundColor: permission.is_visible ? '#e8f5e8' : '#ffeaea',
          color: permission.is_visible ? '#2e7d32' : '#d32f2f'
        }}>
          {permission.is_visible ? 'Visible' : 'Hidden'}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const groupedPermissions = groupPermissionsByParent();
  const rootPermissions = groupedPermissions.root || [];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        width: '90%',
        maxWidth: 600,
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '20px',
              fontWeight: 'bold',
              color: '#1976d2'
            }}>
              Manage Sidebar for {user?.full_name}
            </h2>
            <p style={{
              margin: '4px 0 0',
              fontSize: '14px',
              color: '#666'
            }}>
              {hasUserPermissions ? 'Custom permissions' : 'Using role-based permissions'} • {user?.role_name?.replace(/_/g, ' ')}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: 8,
              borderRadius: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <MdClose size={24} style={{ color: '#666' }} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: 0
        }}>
          {loading ? (
            <div style={{
              padding: 40,
              textAlign: 'center',
              color: '#666'
            }}>
              Loading permissions...
            </div>
          ) : error ? (
            <div style={{
              padding: 40,
              textAlign: 'center',
              color: '#f44336'
            }}>
              {error}
            </div>
          ) : (
            <div>
              {!hasUserPermissions && (
                <div style={{
                  padding: '16px 24px',
                  backgroundColor: '#fff3cd',
                  borderBottom: '1px solid #e0e0e0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12
                }}>
                  <MdRefresh size={20} style={{ color: '#856404' }} />
                  <div style={{ flex: 1, fontSize: '14px', color: '#856404' }}>
                    This user is using default role permissions. Click "Save Changes" to create custom permissions.
                  </div>
                </div>
              )}

              {/* Search and Quick Actions Bar */}
              <div style={{
                padding: '12px 24px',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #e0e0e0'
              }}>
                {/* Search Input */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 12,
                  gap: 12
                }}>
                  <input
                    type="text"
                    placeholder="Search menu items..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: 4,
                      fontSize: '14px',
                      outline: 'none'
                    }}
                  />
                  {searchFilter && (
                    <button
                      onClick={() => setSearchFilter('')}
                      style={{
                        padding: '8px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        color: '#666'
                      }}
                    >
                      <MdClose size={16} />
                    </button>
                  )}
                </div>

                {/* Quick Actions */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}>
                  <div style={{
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#666'
                  }}>
                    Select sidebar items for {user?.full_name}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={selectAllPermissions}
                      style={{
                        padding: '4px 12px',
                        fontSize: '12px',
                        border: '1px solid #1976d2',
                        borderRadius: 4,
                        backgroundColor: 'white',
                        color: '#1976d2',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Select All
                    </button>
                    <button
                      onClick={deselectAllPermissions}
                      style={{
                        padding: '4px 12px',
                        fontSize: '12px',
                        border: '1px solid #f44336',
                        borderRadius: 4,
                        backgroundColor: 'white',
                        color: '#f44336',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Deselect All
                    </button>
                  </div>
                </div>
              </div>
              {rootPermissions.map(permission => (
                <div key={permission.permission_id}>
                  {renderPermissionItem(permission)}
                  {groupedPermissions[permission.menu_item_key] && 
                    groupedPermissions[permission.menu_item_key].map(childPermission => 
                      renderPermissionItem(childPermission, true)
                    )
                  }
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{
            fontSize: '14px',
            color: '#666'
          }}>
            {permissions.filter(p => p.is_visible).length} of {permissions.length} items selected
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onClose}
              style={{
                padding: '8px 16px',
                border: '1px solid #ddd',
                borderRadius: 4,
                backgroundColor: 'white',
                color: '#666',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={savePermissions}
              disabled={saving}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: 4,
                backgroundColor: saving ? '#ccc' : '#1976d2',
                color: 'white',
                cursor: saving ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 8
              }}
            >
              <MdSave size={16} />
              {saving ? 'Saving...' : hasUserPermissions ? 'Save Changes' : 'Create Custom Permissions'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserSidebarPermissionsModal;
